import { useEffect, useMemo, useState } from "react";
import { http, apiError } from "@/lib/api";
import { Moon, TrendUp } from "@phosphor-icons/react";

/**
 * SleepHistory — dedicated sleep view with three panels:
 *  1. Live weekly running total (this ISO week's sleep so far)
 *  2. Bar chart of the last 30 nights of sleep
 *  3. Scrollable table of every logged night
 * Used on the client's My Stats page (scope="mine") and on the coach's
 * client detail page (scope="client", clientId=...).
 */
export default function SleepHistory({ scope = "mine", clientId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setErr("");
      try {
        const url = scope === "client"
          ? `/coach/clients/${clientId}/activity-log?days=30`
          : `/client/activity-log?days=30`;
        const { data } = await http.get(url);
        setData(data);
      } catch (e) { setErr(apiError(e)); }
    })();
  }, [scope, clientId]);

  // Slice + derive
  const derived = useMemo(() => {
    if (!data) return null;
    const min = data.sleep_minimum_hours || 6;
    // data.days is newest first; reverse for chart (oldest → newest)
    const chartRows = [...data.days].reverse().map((d) => ({
      date: d.date,
      hours: d.sleep_hours ?? null,
    }));
    // This ISO week (Mon-today) running total
    const now = new Date();
    const dow = (now.getDay() + 6) % 7; // 0 = Mon
    const monday = new Date(now); monday.setDate(now.getDate() - dow); monday.setHours(0, 0, 0, 0);
    const mondayIso = monday.toISOString().slice(0, 10);
    const todayIso = now.toISOString().slice(0, 10);
    let weekTotal = 0; let weekNights = 0;
    for (const r of chartRows) {
      if (r.date >= mondayIso && r.date <= todayIso && r.hours != null) {
        weekTotal += r.hours;
        weekNights += 1;
      }
    }
    const rowsWithData = chartRows.filter((r) => r.hours != null);
    const allTimeAvg = rowsWithData.length ? rowsWithData.reduce((a, b) => a + b.hours, 0) / rowsWithData.length : 0;
    const maxHours = Math.max(10, ...rowsWithData.map((r) => r.hours));
    // Table entries newest-first (only nights actually logged)
    const tableRows = [...data.days].filter((d) => d.sleep_hours != null);
    return {
      min,
      weekTotal: Math.round(weekTotal * 10) / 10,
      weekNights,
      weekAvg: weekNights ? Math.round((weekTotal / weekNights) * 10) / 10 : 0,
      chartRows,
      maxHours,
      tableRows,
      allTimeAvg: Math.round(allTimeAvg * 10) / 10,
    };
  }, [data]);

  if (err) return <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333]" data-testid="sleep-history-error">{err}</div>;
  if (!derived) return null;

  const { min, weekTotal, weekNights, weekAvg, chartRows, maxHours, tableRows, allTimeAvg } = derived;

  return (
    <div className="id-card" data-testid="sleep-history">
      <div className="flex items-center gap-2 mb-4">
        <Moon size={16} weight="fill" className="text-indigo-400" />
        <div className="id-label">Sleep tracker</div>
      </div>

      {/* Live weekly running total */}
      <div className="grid grid-cols-3 gap-2 mb-6" data-testid="sleep-week-summary">
        <div className="border border-indigo-500/30 bg-indigo-500/5 px-3 py-3">
          <div className="id-label text-indigo-300 mb-1">This week</div>
          <div className="font-oswald text-2xl text-white leading-none">{weekTotal}<span className="text-sm text-zinc-500 ml-1">h</span></div>
          <div className="text-[10px] text-zinc-500 font-manrope mt-1">{weekNights}/7 nights logged</div>
        </div>
        <div className="border border-[#262626] px-3 py-3">
          <div className="id-label text-zinc-500 mb-1">Week avg</div>
          <div className={"font-oswald text-2xl leading-none " + (weekNights === 0 ? "text-zinc-600" : weekAvg < min ? "text-[#FF3333]" : "text-[#00CC66]")}>
            {weekNights ? weekAvg : "—"}<span className="text-sm text-zinc-500 ml-1">h</span>
          </div>
          <div className="text-[10px] text-zinc-500 font-manrope mt-1">target {min}h/night</div>
        </div>
        <div className="border border-[#262626] px-3 py-3">
          <div className="id-label text-zinc-500 mb-1 flex items-center gap-1"><TrendUp size={10} weight="bold" /> 30-day avg</div>
          <div className={"font-oswald text-2xl leading-none " + (allTimeAvg === 0 ? "text-zinc-600" : allTimeAvg < min ? "text-[#FF3333]" : "text-[#00CC66]")}>
            {allTimeAvg || "—"}<span className="text-sm text-zinc-500 ml-1">h</span>
          </div>
          <div className="text-[10px] text-zinc-500 font-manrope mt-1">from logged nights</div>
        </div>
      </div>

      {/* Bar chart */}
      <div className="mb-6">
        <div className="id-label text-zinc-500 mb-2">Last 30 nights</div>
        <div className="flex items-end gap-[2px] h-24 border-b border-[#262626]" data-testid="sleep-history-chart">
          {chartRows.map((r) => {
            const pct = r.hours != null ? Math.min(100, (r.hours / maxHours) * 100) : 0;
            const below = r.hours != null && r.hours < min;
            return (
              <div key={r.date} className="flex-1 relative group flex items-end">
                {r.hours != null ? (
                  <div
                    className={"w-full transition-all " + (below ? "bg-[#FF3333]" : "bg-indigo-400")}
                    style={{ height: `${pct}%`, minHeight: "2px" }}
                    title={`${r.date}: ${r.hours}h`}
                    data-testid={`sleep-bar-${r.date}`}
                  />
                ) : (
                  <div className="w-full h-[2px] bg-[#262626]" title={`${r.date}: no data`} />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[9px] text-zinc-600 font-manrope mt-1">
          <span>{chartRows[0]?.date.slice(5)}</span>
          <span>{chartRows[chartRows.length - 1]?.date.slice(5)}</span>
        </div>
      </div>

      {/* Table of logged nights */}
      {tableRows.length > 0 ? (
        <div>
          <div className="id-label text-zinc-500 mb-2">Nightly log</div>
          <div className="max-h-64 overflow-y-auto border border-[#262626]" data-testid="sleep-history-table">
            <table className="w-full text-xs font-manrope">
              <thead className="bg-[#0A0A0A] sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 text-zinc-500 uppercase tracking-[0.15em] text-[10px] font-normal">Date</th>
                  <th className="text-right px-3 py-2 text-zinc-500 uppercase tracking-[0.15em] text-[10px] font-normal">Hours</th>
                  <th className="text-right px-3 py-2 text-zinc-500 uppercase tracking-[0.15em] text-[10px] font-normal">vs target</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((r) => {
                  const delta = r.sleep_hours - min;
                  const below = delta < 0;
                  return (
                    <tr key={r.date} className="border-t border-[#262626]" data-testid={`sleep-row-${r.date}`}>
                      <td className="px-3 py-2 text-white">{r.date}</td>
                      <td className={"px-3 py-2 text-right font-bold " + (below ? "text-[#FF3333]" : "text-[#00CC66]")}>{r.sleep_hours}h</td>
                      <td className={"px-3 py-2 text-right " + (below ? "text-[#FF3333]" : "text-zinc-400")}>
                        {below ? "" : "+"}{Math.round(delta * 10) / 10}h
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-xs text-zinc-500 font-manrope italic">No sleep hours logged in the last 30 days.</div>
      )}
    </div>
  );
}
